import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { get, includes, map, size } from "lodash";

import styles from "./PaymentProcessing.module.scss";
import Logo from "../../assets/Logo.png";
import Loader from "../../components/loader/Loader";
import Button from "../../components/button/Button";
import { routesConstants } from "../../constants";
import { getTransactionStatus } from "../../services/user.service";
import axios from "../../axios";
import userStore from "../../zustand/user.store";
import playlistStore from "../../zustand/playlist.store";
import likedSongsStore from "../../zustand/likedSong.store";
import disLikedSongsStore from "../../zustand/dislikedSong.store";
import favoriteSongStore from "../../zustand/favoriteSong.store";
import ignoredSongStore from "../../zustand/ignoreSong.store";
import SchedulesStore from "../../zustand/Schedule.store";
import musicPlayerStore from "../../zustand/musicPlayer.store";
import { appActions, userActions } from "../../actions";
import {
  storeAuthInIndexDB,
  storeUserInIndexDB,
} from "../../helpers/indexDb.helpers";
import { billingAccessHelpers, generalHelpers } from "../../helpers";

const DEFAULT_POLL_INTERVAL_MS = 2000;

function safeGetStoredCheckout(transactionId) {
  if (!transactionId) return null;
  try {
    const raw = sessionStorage.getItem(`billing:checkout:${transactionId}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

function normalizePaymentStatus(res) {
  return (
    res?.data?.data?.payment_status ||
    res?.data?.payment_status ||
    res?.data?.status
  );
}

const PaymentProcessing = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const hasFinalizedRef = useRef(false);

  const transactionId = searchParams.get("transactionId");

  const checkout = useMemo(() => {
    const fromState = get(location, "state.checkout", null);
    return fromState || safeGetStoredCheckout(transactionId);
  }, [location, transactionId]);

  const pollIntervalMs = useMemo(() => {
    const v = get(checkout, "poll.pollIntervalMs", null);
    return typeof v === "number" && v > 0 ? v : DEFAULT_POLL_INTERVAL_MS;
  }, [checkout]);

  const {
    user,
    setUser,
    setAuthentication,
    userOffline,
  } = userStore((state) => ({
    user: state.user,
    setUser: state.setUser,
    setAuthentication: state.setAuthentication,
    userOffline: state.userOffline,
  }));

  const { setPlaylists } = playlistStore((state) => ({
    setPlaylists: state.setPlaylists,
  }));
  const { setSongs: setLikedSongs } = likedSongsStore((state) => ({
    setSongs: state.setSongs,
  }));
  const { setSongs: setDislikeSongs } = disLikedSongsStore((state) => ({
    setSongs: state.setSongs,
  }));
  const { setSongs: setFavouriteSongs } = favoriteSongStore((state) => ({
    setSongs: state.setSongs,
  }));
  const { setSongs: setIgnoredSongs } = ignoredSongStore((state) => ({
    setSongs: state.setSongs,
  }));
  const { setSchedules } = SchedulesStore((state) => ({
    setSchedules: state.setSchedules,
  }));
  const { setPlayList } = musicPlayerStore((state) => ({
    setPlayList: state.setPlayList,
  }));

  const findFirstUnIgnoredSongIndex = useCallback((songs, ignoredSongsArr) => {
    const ignoredSongIds = map(ignoredSongsArr, (i) => i.id);
    let index = 0;
    for (let i = 0; i < size(songs); i++) {
      if (!includes(ignoredSongIds, songs[i].id)) {
        index = i;
        break;
      }
    }
    return index;
  }, []);

  const refreshUserAndAppState = useCallback(async () => {
    const userId = get(user, "id", "");
    if (!userId) return false;

    const userRes = await userActions.fetchUserAction(userId, userOffline);
    const code = get(userRes, "data.code", "");
    if (code !== "OK") return false;

    const authData = {
      isAuthenticated: true,
      isPhoneVerified: true,
      isBusinessVerified: true,
      isPrimium: true,
    };
    storeAuthInIndexDB(authData, "authentication-data");

    const {
      user: freshUser,
      playlists,
      likedSongs,
      ignoredSongs,
      favouriteSongs,
      dislikedSongs,
      schedules,
      favoritePlaylist,
    } = await appActions.fetchAppData(userId, userOffline);

    const indexToStore = findFirstUnIgnoredSongIndex(
      favoritePlaylist?.songs,
      ignoredSongs
    );

    await storeUserInIndexDB(freshUser, "user-data");
    setUser(freshUser);
    setPlaylists(playlists);
    setFavouriteSongs(favouriteSongs);
    setLikedSongs(likedSongs);
    setDislikeSongs(dislikedSongs);
    setIgnoredSongs(ignoredSongs);
    setSchedules(schedules);
    setPlayList(favoritePlaylist, indexToStore);

    setAuthentication(authData);
    return true;
  }, [
    user,
    userOffline,
    findFirstUnIgnoredSongIndex,
    setUser,
    setPlaylists,
    setFavouriteSongs,
    setLikedSongs,
    setDislikeSongs,
    setIgnoredSongs,
    setSchedules,
    setPlayList,
    setAuthentication,
  ]);

  const [status, setStatus] = useState("PENDING");
  const [message, setMessage] = useState("Waiting for payment confirmation...");

  useEffect(() => {
    // If Tranzila redirects inside the iframe, break out of it.
    try {
      if (window.top && window.top !== window.self) {
        window.top.location.href = window.location.href;
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!transactionId) return;

    setStatus("PENDING");
    setMessage("Waiting for payment confirmation...");

    const intervalId = setInterval(async () => {
      try {
        const statusUrl = get(checkout, "poll.transactionStatusUrl", null);
        const res = statusUrl
          ? await axios.get(statusUrl)
          : await getTransactionStatus(transactionId);
        const paymentStatus = normalizePaymentStatus(res) || "PENDING";
        setStatus(paymentStatus);

        if (paymentStatus === "SUCCESS") {
          clearInterval(intervalId);
          if (hasFinalizedRef.current) return;
          hasFinalizedRef.current = true;
          setMessage("Payment successful! Setting up your account...");

          try {
            const ok = await refreshUserAndAppState();
            if (ok) {
              const returnTo = (() => {
                const fromState = get(location, "state.returnTo", null);
                if (fromState) return fromState;
                try {
                  const stored = sessionStorage.getItem("billing:returnTo");
                  if (stored) return stored;
                } catch (e) {
                  // ignore
                }
                return null;
              })();

              try {
                sessionStorage.removeItem("billing:returnTo");
              } catch (e) {
                // ignore
              }

              // Show success snackbar
              generalHelpers.toaster("success", "Payment successful! Welcome to 4Play.");

              if (returnTo && billingAccessHelpers.isPlayerRoute(returnTo)) {
                navigate(returnTo, { replace: true });
              } else {
                // Redirect to Profile page on success
                navigate(routesConstants.ROUTES.PROFILE, { replace: true });
              }
            } else {
              generalHelpers.toaster("success", "Payment successful! Welcome to 4Play.");
              navigate(routesConstants.ROUTES.PROFILE, { replace: true });
            }
          } catch (e) {
            generalHelpers.toaster("success", "Payment successful!");
            navigate(routesConstants.ROUTES.PROFILE, { replace: true });
          }
        } else if (paymentStatus === "FAILED") {
          clearInterval(intervalId);
          if (hasFinalizedRef.current) return;
          hasFinalizedRef.current = true;
          setMessage("Payment failed. Redirecting to plans...");
          
          // Show error snackbar and redirect to Plans page
          generalHelpers.toaster("error", "Payment failed. Please try again.");
          
          // Short delay to let user see the message before redirect
          setTimeout(() => {
            navigate(routesConstants.ROUTES.PACKAGES, { replace: true });
          }, 1500);
        } else {
          // PENDING -> keep polling
        }
      } catch (e) {
        clearInterval(intervalId);
        if (hasFinalizedRef.current) return;
        hasFinalizedRef.current = true;
        setStatus("FAILED");
        setMessage("We couldn't confirm your payment. Redirecting to plans...");
        
        // Show error snackbar and redirect to Plans page
        generalHelpers.toaster("error", "We couldn't confirm your payment. Please try again.");
        
        setTimeout(() => {
          navigate(routesConstants.ROUTES.PACKAGES, { replace: true });
        }, 1500);
      }
    }, pollIntervalMs);

    return () => clearInterval(intervalId);
  }, [transactionId, pollIntervalMs, refreshUserAndAppState, navigate, location]);

  if (!transactionId) {
    return (
      <div className={styles.PaymentProcessing}>
        <div className={styles.container}>
          <div className={styles.header}>
            <img src={Logo} alt="4Play" className={styles.logo} />
          </div>
          <h1 className={styles.title}>Payment Processing</h1>
          <p className={styles.subtitle}>Missing transaction id.</p>
          <div className={styles.actions}>
            <Button onClick={() => navigate(routesConstants.ROUTES.PACKAGES)}>
              Back to Plans
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.PaymentProcessing}>
      <div className={styles.container}>
        <div className={styles.header}>
          <img src={Logo} alt="4Play" className={styles.logo} />
        </div>

        <h1 className={styles.title}>Payment Processing</h1>
        <p className={styles.subtitle}>{message}</p>

        {status === "PENDING" ? (
          <Loader />
        ) : (
          <div className={styles.statusPill}>Status: {status}</div>
        )}

        {status === "FAILED" && (
          <div className={styles.actions}>
            <p className={styles.redirectNote}>Redirecting you back to plans...</p>
            <Button onClick={() => navigate(routesConstants.ROUTES.PACKAGES, { replace: true })}>
              Go to Plans Now
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentProcessing;

